import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Globe, AlertTriangle, BarChart3,
  Calendar, ArrowRight, BookOpen, ShieldCheck, Wallet, Briefcase,
  ShoppingCart, Coins, TrendingUp, Smartphone, Heart, Users,
  type LucideIcon,
} from 'lucide-react';
import { supabase, ScamCheckPost, BlogPost } from '../lib/supabase';

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  'Mobile Money': Smartphone,
  'Crypto': Coins,
  'Forex': TrendingUp,
  'Remote Jobs': Briefcase,
  'Jobs': Briefcase,
  'Online Shopping': ShoppingCart,
  'Online': Globe,
  'Offline': Users,
  'Loan Apps': Wallet,
  'Romance': Heart,
  'Investment': TrendingUp,
  'Trading': BarChart3,
  'Freelancing': Briefcase,
  'Rent': ShoppingCart,
  'Other': ShieldCheck,
};

function getCategoryIcon(category: string): LucideIcon {
  return CATEGORY_ICONS[category] || ShieldCheck;
}

const VERDICT_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  confirmed_scam: { bg: 'bg-flag-500/10', text: 'text-flag-500', label: 'High Risk' },
  high_risk: { bg: 'bg-flag-500/10', text: 'text-flag-600', label: 'High Risk' },
  likely_suspicious: { bg: 'bg-scan-500/10', text: 'text-scan-400', label: 'Suspicious' },
  proceed_with_caution: { bg: 'bg-ash-400/10', text: 'text-ash-400', label: 'Caution' },
  safe: { bg: 'bg-sage-500/10', text: 'text-sage-500', label: 'Safe' },
};

const TYPE_BADGES: Record<string, { label: string; bg: string; text: string }> = {
  domain: { label: 'Domain Scan', bg: 'bg-sage-500/10', text: 'text-sage-500' },
  scenario: { label: 'Scenario Scan', bg: 'bg-scan-500/10', text: 'text-scan-400' },
  legacy: { label: 'Report', bg: 'bg-ink-700', text: 'text-ash-400' },
};

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getScoreColor(score: number | null): string {
  if (score === null) return 'text-ash-400';
  if (score >= 80) return 'text-flag-500';
  if (score >= 60) return 'text-flag-600';
  if (score >= 40) return 'text-scan-400';
  return 'text-sage-500';
}

function CardSkeleton() {
  return (
    <div className="bg-ink-800 rounded-xl border border-line p-4 animate-pulse">
      <div className="h-3 bg-ink-700 rounded w-20 mb-3"></div>
      <div className="h-4 bg-ink-700 rounded w-full mb-2"></div>
      <div className="h-3 bg-ink-700 rounded w-2/3 mb-3"></div>
      <div className="h-3 bg-ink-700 rounded w-1/3"></div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. BROWSE BY CATEGORY
// ═══════════════════════════════════════════════════════════════════════════════
export function CategoryBrowser({ onCategoryClick }: { onCategoryClick: (category: string) => void }) {
  const [categories, setCategories] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('scam_checks').select('category').eq('published', true);
      if (data) {
        const counts: Record<string, number> = {};
        data.forEach((row: { category: string }) => {
          const cat = row.category || 'Other';
          counts[cat] = (counts[cat] || 0) + 1;
        });
        setCategories(
          Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
        );
      }
      setLoading(false);
    };
    load();
  }, []);

  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -el.clientWidth * 0.75 : el.clientWidth * 0.75, behavior: 'smooth' });
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-5">
        <span className="text-xs font-bold text-flag-500 uppercase tracking-wider">Browse</span>
        <h2 className="text-xl sm:text-2xl font-extrabold text-snow mt-1">Search by Category</h2>
      </div>

      {loading ? (
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex-shrink-0 w-36 h-32 bg-ink-800 rounded-xl border border-line animate-pulse" />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No categories available yet.</p>
      ) : (
        <div className="relative group">
          <button
            onClick={() => scrollCarousel('left')}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-ink-800 border border-line items-center justify-center text-ash-400 hover:text-snow hover:border-ash-400 shadow-md transition opacity-0 group-hover:opacity-100"
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>

          <div ref={carouselRef} className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mb-2 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {categories.map((cat) => {
              const Icon = getCategoryIcon(cat.name);
              return (
                <button
                  key={cat.name}
                  onClick={() => onCategoryClick(cat.name)}
                  className="group/card flex-shrink-0 w-36 sm:w-40 bg-ink-800 rounded-xl border border-line p-4 text-left hover:border-flag-500/40 hover:shadow-md transition-all snap-start"
                >
                  <div className="w-10 h-10 rounded-lg bg-flag-500/10 flex items-center justify-center mb-3 group-hover/card:scale-110 transition-transform">
                    <Icon className="w-5 h-5 text-flag-500" />
                  </div>
                  <h3 className="text-sm font-bold text-snow leading-tight mb-1">{cat.name}</h3>
                  <p className="text-xs text-ash-400">{cat.count} {cat.count === 1 ? 'report' : 'reports'}</p>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => scrollCarousel('right')}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-ink-800 border border-line items-center justify-center text-ash-400 hover:text-snow hover:border-ash-400 shadow-md transition opacity-0 group-hover:opacity-100"
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. RECENT SCAM INVESTIGATIONS
// ═══════════════════════════════════════════════════════════════════════════════
export function RecentInvestigations({ onPostClick }: { onPostClick: (postId: string, slug: string) => void }) {
  const [recent, setRecent] = useState<ScamCheckPost[]>([]);
  const [topRisk, setTopRisk] = useState<ScamCheckPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [recentRes, topRiskRes] = await Promise.all([
        supabase.from('scam_checks').select('*').eq('published', true).in('post_type', ['domain', 'scenario']).order('created_at', { ascending: false }).limit(3),
        supabase.from('scam_checks').select('*').eq('published', true).in('post_type', ['domain', 'scenario']).not('score', 'is', null).order('score', { ascending: false }).limit(3),
      ]);
      if (recentRes.data) setRecent(recentRes.data as ScamCheckPost[]);
      if (topRiskRes.data) setTopRisk(topRiskRes.data as ScamCheckPost[]);
      setLoading(false);
    };
    load();
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-5">
        <span className="text-xs font-bold text-flag-500 uppercase tracking-wider">Investigations</span>
        <h2 className="text-xl sm:text-2xl font-extrabold text-snow mt-1">Recent Scam Investigations</h2>
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-flag-500" />
          Recently Investigated
        </h3>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4"><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
        ) : recent.length === 0 ? (
          <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No investigations published yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recent.map((inv) => (
              <InvestigationCard key={inv.id} post={inv} onClick={() => onPostClick(inv.id, inv.slug)} />
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4 text-flag-500" />
          Highest Risk Scores
        </h3>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4"><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
        ) : topRisk.length === 0 ? (
          <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No scored investigations yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topRisk.map((inv) => (
              <InvestigationCard key={inv.id} post={inv} onClick={() => onPostClick(inv.id, inv.slug)} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. FEATURED BLOGS
// ═══════════════════════════════════════════════════════════════════════════════
export function FeaturedBlogs({ onBlogClick }: { onBlogClick: (slug: string) => void }) {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('ai_blog_posts').select('*').eq('published', true).order('published_at', { ascending: false }).limit(3);
      if (data) setBlogs(data as BlogPost[]);
      setLoading(false);
    };
    load();
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <span className="text-xs font-bold text-flag-500 uppercase tracking-wider">Blog</span>
          <h2 className="text-xl sm:text-2xl font-extrabold text-snow mt-1">Latest from the Blog</h2>
        </div>
        <button onClick={() => onBlogClick('')} className="inline-flex items-center gap-1 text-sm font-semibold text-flag-500 hover:text-flag-600 transition whitespace-nowrap">
          View All Articles <ArrowRight size={14} />
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4"><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
      ) : blogs.length === 0 ? (
        <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No blog articles published yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {blogs.map((post) => (
            <BlogCard key={post.id} post={post} onClick={() => onBlogClick(post.slug)} />
          ))}
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARDS
// ═══════════════════════════════════════════════════════════════════════════════
function InvestigationCard({ post, onClick }: { post: ScamCheckPost; onClick: () => void }) {
  const typeBadge = TYPE_BADGES[post.post_type] || TYPE_BADGES.legacy;
  const verdict = VERDICT_STYLES[post.verdict || ''] || VERDICT_STYLES.proceed_with_caution;
  const scoreColor = getScoreColor(post.score);

  return (
    <article onClick={onClick} className="group bg-ink-800 rounded-xl border border-line overflow-hidden cursor-pointer hover:shadow-md hover:border-ash-400 transition-all">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${typeBadge.bg} ${typeBadge.text}`}>
            {post.post_type === 'domain' ? <Globe size={10} /> : <AlertTriangle size={10} />}
            {typeBadge.label}
          </span>
          {post.score !== null && (
            <span className={`text-sm font-extrabold ${scoreColor} ml-auto`}>
              {post.score}<span className="text-[10px] font-medium text-ash-500">/100</span>
            </span>
          )}
        </div>
        <h3 className="text-sm font-bold text-snow group-hover:text-flag-500 transition-colors line-clamp-2 leading-snug mb-2">{post.title}</h3>
        <p className="text-xs text-ash-400 line-clamp-2 leading-relaxed mb-3">{post.excerpt || 'No description available.'}</p>
        <div className="flex items-center justify-between pt-2 border-t border-line">
          <span className="flex items-center gap-1 text-[10px] text-ash-500"><Calendar size={10} /> {formatDate(post.published_at || post.created_at)}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${verdict.bg} ${verdict.text}`}>{verdict.label}</span>
        </div>
        <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-flag-500 group-hover:gap-2 transition-all">View Report <ArrowRight size={12} /></div>
      </div>
    </article>
  );
}

function BlogCard({ post, onClick }: { post: BlogPost; onClick: () => void }) {
  return (
    <article onClick={onClick} className="group bg-ink-800 rounded-xl border border-line overflow-hidden cursor-pointer hover:shadow-md hover:border-ash-400 transition-all">
      <div className="aspect-[16/9] overflow-hidden relative bg-ink-700">
        {post.cover_image_url ? (
          <img src={post.cover_image_url} alt={post.h1} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ink-700 to-ink-600">
            <BookOpen className="w-8 h-8 text-ash-500" />
          </div>
        )}
        {post.topic && (
          <div className="absolute top-2 left-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-snow/90 backdrop-blur-sm text-[10px] font-semibold rounded-full text-ink-900 shadow-sm">{post.topic}</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-bold text-snow group-hover:text-flag-500 transition-colors line-clamp-2 leading-snug mb-2">{post.h1}</h3>
        <p className="text-xs text-ash-400 line-clamp-2 leading-relaxed mb-3">{post.meta_description}</p>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-[10px] text-ash-500"><Calendar size={10} /> {formatDate(post.published_at)}</span>
          <span className="text-xs font-semibold text-flag-500 group-hover:underline flex items-center gap-0.5">Read More <ArrowRight size={11} /></span>
        </div>
      </div>
    </article>
  );
}
